export interface PerfChartSymbol {
  symbol: string;
  id: number;
}

export function isPerfChartSymbol(unknownValue: unknown): unknownValue is PerfChartSymbol {
  return (
    typeof unknownValue === 'object' &&
    unknownValue !== null &&
    'symbol' in unknownValue &&
    'id' in unknownValue &&
    typeof unknownValue.symbol === 'string' &&
    typeof unknownValue.id === 'number'
  );
}

export interface Indicator {
  indicatorName: string;
  parameters: string;
  indicatorPosition: string;
  indicatorPriceStyle: string;
  indicatorColor: string;
  indicatorHeight: string;
  indicatorOpacity: string;
  overlayName: string;
  overlayParameters: string;
  id: number;
}

export function isIndicator(unknownValue: unknown): unknownValue is Indicator {
  return (
    typeof unknownValue === 'object' &&
    unknownValue !== null &&
    'indicatorName' in unknownValue &&
    'parameters' in unknownValue &&
    'indicatorPosition' in unknownValue &&
    'indicatorPriceStyle' in unknownValue &&
    'indicatorColor' in unknownValue &&
    'indicatorHeight' in unknownValue &&
    'indicatorOpacity' in unknownValue &&
    'overlayName' in unknownValue &&
    'overlayParameters' in unknownValue &&
    'id' in unknownValue &&
    typeof unknownValue.indicatorName === 'string' &&
    typeof unknownValue.parameters === 'string' &&
    typeof unknownValue.indicatorPosition === 'string' &&
    typeof unknownValue.indicatorPriceStyle === 'string' &&
    typeof unknownValue.indicatorColor === 'string' &&
    typeof unknownValue.indicatorHeight === 'string' &&
    typeof unknownValue.indicatorOpacity === 'string' &&
    typeof unknownValue.overlayName === 'string' &&
    typeof unknownValue.overlayParameters === 'string' &&
    typeof unknownValue.id === 'number'
  );
}

export interface Overlay {
  overlayName: string;
  parameters: string;
  overlayStyle: string;
  overlayColor: string;
  overlayOpacity: string;
  id: number;
}

export function isOverlay(unknownValue: unknown): unknownValue is Overlay {
  return (
    typeof unknownValue === 'object' &&
    unknownValue !== null &&
    'overlayName' in unknownValue &&
    'parameters' in unknownValue &&
    'overlayStyle' in unknownValue &&
    'overlayColor' in unknownValue &&
    'overlayOpacity' in unknownValue &&
    'id' in unknownValue &&
    typeof unknownValue.overlayName === 'string' &&
    typeof unknownValue.parameters === 'string' &&
    typeof unknownValue.overlayStyle === 'string' &&
    typeof unknownValue.overlayColor === 'string' &&
    typeof unknownValue.overlayOpacity === 'string' &&
    typeof unknownValue.id === 'number'
  );
}

export interface Chart extends ChartStyle {
  symbol: string;
}

export function isChart(unknownValue: unknown): unknownValue is Chart {
  return (
    isChartStyle(unknownValue) &&
    'symbol' in unknownValue &&
    typeof unknownValue.symbol === 'string'
  );
}

export interface ChartStyle {
  chartType: string;
  period: string;
  range: string;
  rangeBar: string;
  rangeGap: string;
  rangeExtraBars: number;
  rangeDays: string;
  rangeMonths: string;
  rangeYears: string;
  rangeStartDate: string;
  rangeEndDate: string;
  perfChartSymbols: PerfChartSymbol[];
  useMainChartSymbolAsBaseline: boolean;
  colorScheme: string;
  customColorSchemeBackgroundColor: string;
  customColorSchemeGridColor: string;
  customColorSchemeTextColor: string;
  customColorSchemeIndicatorColor: string;
  upColor: string;
  downColor: string;
  opacity: string;
  chartSize: string;
  customWidth: number;
  customHeight: number;
  chartGrid: string;
  legends: string;
  isLogScale: boolean;
  extendedHours: boolean;
  fullQuote: boolean;
  priceLabels: boolean;
  yAxisLabels: boolean;
  solidCandles: boolean;
  colorVolume: boolean;
  zoomThumbnail: boolean;
  chartSettingsVolumeMenuValue: string;
  kagiReversal: string;
  kagiUnit: string;
  kagiPriceField: string;
  renkoBoxSize: string;
  renkoUnit: string;
  renkoPriceField: string;
  sortedIndicators: Indicator[];
  sortedOverlays: Overlay[]
}

export function isChartStyle(unknownValue: unknown): unknownValue is ChartStyle {
  return (
    typeof unknownValue === 'object' &&
    unknownValue !== null &&
    'chartType' in unknownValue &&
    'period' in unknownValue &&
    'range' in unknownValue &&
    'rangeBar' in unknownValue &&
    'rangeGap' in unknownValue &&
    'rangeExtraBars' in unknownValue &&
    'rangeDays' in unknownValue &&
    'rangeMonths' in unknownValue &&
    'rangeYears' in unknownValue &&
    'rangeStartDate' in unknownValue &&
    'rangeEndDate' in unknownValue &&
    'perfChartSymbols' in unknownValue &&
    'useMainChartSymbolAsBaseline' in unknownValue &&
    'colorScheme' in unknownValue &&
    'customColorSchemeBackgroundColor' in unknownValue &&
    'customColorSchemeGridColor' in unknownValue &&
    'customColorSchemeTextColor' in unknownValue &&
    'customColorSchemeIndicatorColor' in unknownValue &&
    'upColor' in unknownValue &&
    'downColor' in unknownValue &&
    'opacity' in unknownValue &&
    'chartSize' in unknownValue &&
    'customWidth' in unknownValue &&
    'customHeight' in unknownValue &&
    'chartGrid' in unknownValue &&
    'legends' in unknownValue &&
    'isLogScale' in unknownValue &&
    'extendedHours' in unknownValue &&
    'fullQuote' in unknownValue &&
    'priceLabels' in unknownValue &&
    'yAxisLabels' in unknownValue &&
    'solidCandles' in unknownValue &&
    'colorVolume' in unknownValue &&
    'zoomThumbnail' in unknownValue &&
    'chartSettingsVolumeMenuValue' in unknownValue &&
    'kagiReversal' in unknownValue &&
    'kagiUnit' in unknownValue &&
    'kagiPriceField' in unknownValue &&
    'renkoBoxSize' in unknownValue &&
    'renkoUnit' in unknownValue &&
    'renkoPriceField' in unknownValue &&
    'sortedIndicators' in unknownValue &&
    'sortedOverlays' in unknownValue &&
    typeof unknownValue.chartType === 'string' &&
    typeof unknownValue.period === 'string' &&
    typeof unknownValue.range === 'string' &&
    typeof unknownValue.rangeBar === 'string' &&
    typeof unknownValue.rangeGap === 'string' &&
    typeof unknownValue.rangeExtraBars === 'number' &&
    typeof unknownValue.rangeDays === 'string' &&
    typeof unknownValue.rangeMonths === 'string' &&
    typeof unknownValue.rangeYears === 'string' &&
    typeof unknownValue.rangeStartDate === 'string' &&
    typeof unknownValue.rangeEndDate === 'string' &&
    Array.isArray(unknownValue.perfChartSymbols) &&
    unknownValue.perfChartSymbols.every(isPerfChartSymbol) &&
    typeof unknownValue.useMainChartSymbolAsBaseline === 'boolean' &&
    typeof unknownValue.colorScheme === 'string' &&
    typeof unknownValue.customColorSchemeBackgroundColor === 'string' &&
    typeof unknownValue.customColorSchemeGridColor === 'string' &&
    typeof unknownValue.customColorSchemeTextColor === 'string' &&
    typeof unknownValue.customColorSchemeIndicatorColor === 'string' &&
    typeof unknownValue.upColor === 'string' &&
    typeof unknownValue.downColor === 'string' &&
    typeof unknownValue.opacity === 'string' &&
    typeof unknownValue.chartSize === 'string' &&
    typeof unknownValue.customWidth === 'number' &&
    typeof unknownValue.customHeight === 'number' &&
    typeof unknownValue.chartGrid === 'string' &&
    typeof unknownValue.legends === 'string' &&
    typeof unknownValue.isLogScale === 'boolean' &&
    typeof unknownValue.extendedHours === 'boolean' &&
    typeof unknownValue.fullQuote === 'boolean' &&
    typeof unknownValue.priceLabels === 'boolean' &&
    typeof unknownValue.yAxisLabels === 'boolean' &&
    typeof unknownValue.solidCandles === 'boolean' &&
    typeof unknownValue.colorVolume === 'boolean' &&
    typeof unknownValue.zoomThumbnail === 'boolean' &&
    typeof unknownValue.chartSettingsVolumeMenuValue === 'string' &&
    typeof unknownValue.kagiReversal === 'string' &&
    typeof unknownValue.kagiUnit === 'string' &&
    typeof unknownValue.kagiPriceField === 'string' &&
    typeof unknownValue.renkoBoxSize === 'string' &&
    typeof unknownValue.renkoUnit === 'string' &&
    typeof unknownValue.renkoPriceField === 'string' &&
    Array.isArray(unknownValue.sortedIndicators) &&
    unknownValue.sortedIndicators.every(isIndicator) &&
    Array.isArray(unknownValue.sortedOverlays) &&
    unknownValue.sortedOverlays.every(isOverlay)
  );
}

export interface SimpleFrontendChart {
  annotations: string;
  chartListListNum: number;
  comments: string;
  name: string;
  htmlSanitizedName: string;
  recid: number;
  searchTags: string[];
  styleCacheId: string;
  styleCacheSettingsOverrides: Partial<ChartStyle>;
  symbol: string;
  temporaryName: string;
  thumbnailUrl: string;
  isForPayingMembersOnly: boolean;
  isPointAndFigureChart: boolean;
}
